import type { RoundItem } from '../lib/api';

/* Finding one headline among a few hundred.
 *
 * ── Why this exists ──
 * There was no search anywhere in the builder. With 43 in intake, 43 in the pool and a collector
 * adding more every week, finding a headline meant browser Ctrl-F — which silently fails on fake
 * pages, because their composer is collapsed and the text is not in the DOM to be found.
 *
 * ── What this is NOT ──
 * Not a second opinion on readiness. The server owns that (`itemIsReady` / `readinessProblems`),
 * and the "Not ready to record" banner is the answer. The flags below are a way to LOOK at a
 * subset — "show me the ones waiting on artwork" — expressed directly in terms of fields the
 * client already holds. If the two ever disagree, the server is right and this is a stale finder.
 */

export type ItemFlag = '' | 'needs-art' | 'no-link' | 'undated';

export interface ItemFilters {
  /** Free text over the headline, the outlet and the link. */
  q: string;
  verdict: '' | 'real' | 'fake';
  flag: ItemFlag;
  /** A decade as its first year — '1990', '2000' — or '' for any. */
  decade: string;
  /* An explicit set of headlines, used by "Show only these" on the readiness banner.
   *
   * An ID set rather than another flag, because the flags cover three problem kinds and the
   * readiness check reports five — and readiness is the SERVER's judgement, which this file
   * deliberately does not duplicate (see the note at the top). Handing it the ids lets it show
   * exactly what the server flagged without re-deriving why.
   *
   * ⚠️ null means "not filtering by id". An EMPTY ARRAY means "match nothing", which is a real
   * state — it is what a Show only these on a resolved problem should produce, not a silent
   * reset to showing everything. */
  ids: string[] | null;
}

export const NO_FILTERS: ItemFilters = { q: '', verdict: '', flag: '', decade: '', ids: null };

export function filtersActive(f: ItemFilters): boolean {
  // `ids` counts even when empty — see the note on the field.
  return Boolean(f.q.trim() || f.verdict || f.flag || f.decade || f.ids);
}

/** '1994-03-02' and '1994' both land in the 1990s. Undated stays undated rather than guessing. */
export function decadeOf(publishedAt: string): string {
  const year = Number(String(publishedAt || '').slice(0, 4));
  if (!Number.isInteger(year) || year < 1000) return '';
  return String(Math.floor(year / 10) * 10);
}

/** Only the decades actually present, oldest first — an empty option is a dead end. */
export function decadesPresent(items: RoundItem[]): string[] {
  const seen = new Set<string>();
  for (const item of items) {
    const d = decadeOf(item.publishedAt);
    if (d) seen.add(d);
  }
  return [...seen].sort();
}

/* Every word must appear somewhere, rather than the whole phrase appearing verbatim. "zoo escape"
   should find "Zoo says escaped bird returned" — a phrase match finds nothing and reads as a
   broken search box. */
function matchesText(item: RoundItem, q: string): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = `${item.headline} ${item.sourceName} ${item.sourceUrl}`.toLowerCase();
  return terms.every((t) => hay.includes(t));
}

function matchesFlag(item: RoundItem, flag: ItemFlag): boolean {
  switch (flag) {
    /* The artwork bottleneck. Only fakes can want art, and a real headline showing up under
       "waiting on artwork" would be nonsense. */
    case 'needs-art': return item.verdict === 'fake' && item.artStatus === 'needs-art';
    // A real headline with no link has no reveal — the one thing that certainly breaks on camera.
    case 'no-link': return item.verdict === 'real' && !item.sourceUrl.trim();
    case 'undated': return !decadeOf(item.publishedAt);
    default: return true;
  }
}

export function filterItems(items: RoundItem[], f: ItemFilters): RoundItem[] {
  if (!filtersActive(f)) return items;
  const q = f.q.trim();
  const only = f.ids ? new Set(f.ids) : null;
  return items.filter((item) => (
    (!only || only.has(item.id))
    && matchesText(item, q)
    && (!f.verdict || item.verdict === f.verdict)
    && matchesFlag(item, f.flag)
    && (!f.decade || decadeOf(item.publishedAt) === f.decade)
  ));
}
